import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { DEFAULT_INTERRUPTSOURCES, Idle } from "@ng-idle/core";
import { Keepalive } from "@ng-idle/keepalive";
import { lastValueFrom } from "rxjs";
import { IdleTimeoutModalComponent } from "../view/shared/idle-timeout-modal/idle-timeout-modal.component";
import { AuditService } from "./audit/audit.service";
import { AuthService } from "./auth/auth.service";
import { ConfigGeneralService } from "./config-general.service";

@Injectable({
  providedIn: "root",
})
export class IdleTimeoutService {
  private readonly DEFAULT_IDLE_TIME = 60; // Default 60 seconds if config is not available
  private readonly DEFAULT_TIMEOUT_TIME = 1; // Minimum timeout for event to fire
  private readonly EXTERNAL_TIMEOUT_CONFIG_VALUE = "user.timeout.external";
  private readonly INTERNAL_TIMEOUT_CONFIG_VALUE = "user.timeout.internal";
  private CONFIG_NAME = this.EXTERNAL_TIMEOUT_CONFIG_VALUE;
  private isInitialized = false; // Track if idle timeout has been initialized
  private isShowingModal = false;

  constructor(
    private idle: Idle,
    private keepalive: Keepalive,
    private authService: AuthService,
    private router: Router,
    private modalService: NgbModal,
    private configGeneralService: ConfigGeneralService,
    private auditService: AuditService,
  ) {}

  /**
   * Initialize idle timeout monitoring with config from server
   * Fetches the idle timeout value from config-general endpoint
   * @returns Promise that resolves when initialization is complete
   */
  public async initializeIdleTimeoutFromConfig(): Promise<void> {
    // Prevent double initialization
    if (this.isInitialized) {
      return;
    }

    try {
      // Determine which config name to use based on environment
      const isInternalUser = this.authService.isInternalUser;
      if (isInternalUser) {
        this.CONFIG_NAME = this.INTERNAL_TIMEOUT_CONFIG_VALUE;
      } else {
        this.CONFIG_NAME = this.EXTERNAL_TIMEOUT_CONFIG_VALUE;
      }

      // Fetch the timeout value from config-general
      const timeoutValue = await lastValueFrom(
        this.configGeneralService.getConfigNumberByName(this.CONFIG_NAME),
      );

      // Use the fetched value or fall back to default
      const idleTime = timeoutValue !== null ? timeoutValue : this.DEFAULT_IDLE_TIME;

      // Initialize with the fetched/default value
      this.initializeIdleTimeout(idleTime, this.DEFAULT_TIMEOUT_TIME);
      this.isInitialized = true;
    } catch (error) {
      // Fall back to default if config fetch fails
      this.initializeIdleTimeout(this.DEFAULT_IDLE_TIME, this.DEFAULT_TIMEOUT_TIME);
      this.isInitialized = true;
    }
  }

  /**
   * Initialize idle timeout monitoring
   * @param idleTime Time in seconds before user is considered idle
   * @param timeoutTime Time in seconds before auto logout after idle (minimum 1 second)
   */
  public initializeIdleTimeout(idleTime: number = 60, timeoutTime: number = 1): void {
    this.idle.setIdle(idleTime);
    this.idle.setTimeout(timeoutTime);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.idle.onTimeout.subscribe(() => {
      if (!this.isShowingModal) {
        this.isShowingModal = true;
        this._logoutUser();
      }
    });

    this.keepalive.interval(15);
    this.reset();
  }

  public watch(): void {
    if (this.authService.isAuthenticated()) {
      this.idle.watch();
    }
  }

  public stop(): void {
    this.idle.stop();
  }

  public reset(): void {
    this.idle.watch();
  }

  private _logoutUser(): void {
    const modalRef = this.modalService.open(IdleTimeoutModalComponent, {
      backdrop: "static",
      keyboard: false,
      centered: true,
    });

    modalRef.result.then(
      async (result) => {
        this.isShowingModal = false;
        if (result === "extend") {
          this.reset();
        } else if (result === "logout") {
          await this._finalise();
          this.router.navigate(["/login"]);
        } else {
          await this._finalise();
          this.router.navigate(["/logged-out"]);
        }
      },
      async () => {
        // Modal dismissed (treat as logout)
        await this._finalise();
        this.router.navigate(["/login"]);
      },
    );
  }

  private async _finalise() {
    this.isShowingModal = false;
    this.idle.stop();
    this.isInitialized = false; // Reset initialization flag on logout

    try {
      await this.auditService.createTimeoutAudit();
    } catch (error) {
      // Handle error if needed
    }

    this.authService.logout();
    this.modalService.dismissAll();
  }
}
